import React, { useState, useEffect, Fragment } from 'react';
import { supabase } from '../../src/lib/supabase';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Dialog, Transition } from '@headlessui/react';

interface PublicGalleryItem {
  id: string;
  creation_task_id: string;
  created_at: string;
  task_id: string;
  poem_title: string;
  poem_content: string;
  task_type: string;
  media_url: string;
  task_created_at: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

// Update modal component
export function MediaPreviewModal({
  isOpen,
  onClose,
  mediaUrl,
  mediaType,
  poemTitle,
  poemContent
}: {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: string;
  poemTitle?: string;
  poemContent?: string;
}) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
              {mediaType === 'image' ? (
                <div className="relative rounded-lg overflow-hidden">
                  <img
                    src={mediaUrl}
                    alt="Preview"
                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  
                  {/* 渐变遮罩 */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
                  
                  {/* 诗歌内容 */}
                  {poemTitle && (
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 
                                transition-transform duration-300 z-10">
                      <h3 className="text-xl font-semibold mb-2 text-white">{poemTitle}</h3>
                      {poemContent && (
                        <p className="text-sm text-gray-300 whitespace-pre-line line-clamp-4">
                          {poemContent}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : mediaType === 'video' ? (
                <div className="relative rounded-lg overflow-hidden">
                  <video
                    src={mediaUrl}
                    controls
                    autoPlay
                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="relative rounded-lg overflow-hidden">
                  {/* 背景图片 */}
                  <img
                    src="https://res.cloudinary.com/dnvmhrx3i/image/upload/q_auto/v1/parlando/generations/rijua0ij7bdi17nv0wdf"
                    alt="Audio Background"
                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                  />
                  
                  {/* 渐变遮罩 */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
                  
                  {/* 音频播放器 */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl px-8 z-20">
                    <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xl text-white/90">
                          {mediaType === 'music' ? '🎵 Music' : '🎙️ Recitation'}
                        </span>
                      </div>
                      <audio
                        src={mediaUrl}
                        controls
                        autoPlay
                        className="w-full h-12"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* 诗歌内容 */}
                  {poemTitle && (
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 
                                transition-transform duration-300 z-10">
                      <h3 className="text-xl font-semibold mb-2 text-white">{poemTitle}</h3>
                      {poemContent && (
                        <p className="text-sm text-gray-300 whitespace-pre-line line-clamp-4">
                          {poemContent}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={onClose}
                className="absolute -top-5 -right-5 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white/80 hover:text-white transition-all hover:scale-105 backdrop-blur-sm"
                aria-label="Close preview"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

const Gallery: React.FC = () => {
  const [items, setItems] = useState<PublicGalleryItem[]>([]);
  const [visibleItems, setVisibleItems] = useState<number>(6);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const headerRef = useScrollAnimation();
  const galleryRef = useScrollAnimation();
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    type: string;
    title?: string;
    content?: string;
  } | null>(null);

  // Add state for video thumbnails
  const [videoThumbnails, setVideoThumbnails] = useState<Record<string, string>>({});

  const fetchGalleryItems = async (retry = 0): Promise<void> => {
    try {
      console.log('Attempting to fetch gallery items, attempt:', retry + 1);
      const { data, error } = await supabase
        .from('public_gallery_view')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      console.log('Fetched gallery items:', data);

      const validatedItems = (data || [])
        .filter(item => {
          if (!item.media_url) {
            console.log('Filtering out item with no media_url:', item);
            return false;
          }
          return true;
        })
        .map(item => ({
          id: item.id as string,
          creation_task_id: item.creation_task_id as string,
          created_at: item.created_at as string,
          task_id: item.task_id as string,
          poem_title: item.poem_title as string,
          poem_content: item.poem_content as string,
          task_type: item.task_type as string,
          media_url: item.media_url as string,
          task_created_at: item.task_created_at as string
        }));

      console.log('Valid items count:', validatedItems.length);

      const shuffledItems = [...validatedItems];
      for (let i = shuffledItems.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledItems[i], shuffledItems[j]] = [shuffledItems[j], shuffledItems[i]];
      }

      setItems(shuffledItems);
      setIsLoading(false);
      setError(null);
      setRetryCount(0);
    } catch (err) {
      console.error('Error fetching gallery items:', err);
      
      if (retry < MAX_RETRIES) {
        console.log(`Retrying in ${RETRY_DELAY}ms... (Attempt ${retry + 1} of ${MAX_RETRIES})`);
        setTimeout(() => {
          setRetryCount(retry + 1);
          fetchGalleryItems(retry + 1);
        }, RETRY_DELAY);
      } else {
        setError('Unable to load gallery items. Please check your connection and try again.');
        setIsLoading(false);
      }
    }
  };

  // Add function to generate video thumbnail
  const generateVideoThumbnail = (videoUrl: string, videoId: string) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.currentTime = 0.1; // Seek to 0.1s to get the first frame
    
    video.addEventListener('loadeddata', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailUrl = canvas.toDataURL();
        setVideoThumbnails(prev => ({
          ...prev,
          [videoId]: thumbnailUrl
        }));
      }
    });
  };

  // Update useEffect to generate thumbnails for videos
  useEffect(() => {
    items.forEach(item => {
      if (item.task_type === 'video' && !videoThumbnails[item.id]) {
        generateVideoThumbnail(item.media_url, item.id);
      }
    });
  }, [items]);

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  if (isLoading) {
    return (
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          {retryCount > 0 && (
            <p className="mt-4 text-gray-400">
              Connecting to server... Attempt {retryCount} of {MAX_RETRIES}
            </p>
          )}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => {
              setIsLoading(true);
              setError(null);
              setRetryCount(0);
              fetchGalleryItems();
            }}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="text-center text-gray-500">
          No items in the gallery yet.
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/3 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={headerRef}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Poetic Creations Gallery
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Discover how Parlando transforms written poetry into mesmerizing visual and auditory experiences
          </p>
        </div>

        <div 
          ref={galleryRef}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 lg:gap-10"
        >
          {items.slice(0, visibleItems).map((item, index) => (
            <div
              key={item.id}
              className="group relative rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-500 shadow-lg shadow-purple-900/20 cursor-pointer"
              style={{ 
                transitionDelay: `${index * 100}ms`,
                aspectRatio: index % 3 === 0 ? '3/4' : index % 3 === 1 ? '1/1' : '4/5'
              }}
              onClick={() => setSelectedMedia({
                url: item.media_url,
                type: item.task_type,
                title: item.poem_title,
                content: item.poem_content
              })}
            >
              {item.media_url && (
                <div className="absolute inset-0">
                  {(() => {
                    if (item.task_type === 'image') {
                      return (
                        <img 
                          src={item.media_url} 
                          alt={item.poem_title}
                          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 
                                  transition-all duration-500 group-hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      );
                    }

                    if (item.task_type === 'video') {
                      return (
                        <div className="absolute inset-0">
                          {videoThumbnails[item.id] && (
                            <img 
                              src={videoThumbnails[item.id]}
                              alt={item.poem_title}
                              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 
                                      transition-all duration-500 group-hover:scale-105"
                            />
                          )}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-4xl text-white/80">🎬</div>
                          </div>
                        </div>
                      );
                    }

                    if (['music', 'recitation'].includes(item.task_type)) {
                      const isMusic = item.task_type === 'music';
                      return (
                        <div className="absolute inset-0">
                          <img 
                            src="https://res.cloudinary.com/dnvmhrx3i/image/upload/q_auto/v1/parlando/generations/rijua0ij7bdi17nv0wdf"
                            alt={item.poem_title}
                            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 
                                    transition-all duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-4xl text-white/80">
                              {isMusic ? '🎵' : '🎙️'}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })()}
                </div>
              )}
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
              
              <div className="absolute top-4 right-4 z-10">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-500/80 text-white backdrop-blur-sm">
                  {item.task_type}
                </span>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 
                           transition-transform duration-300 z-10">
                <h3 className="text-xl font-semibold mb-2 text-white">{item.poem_title}</h3>
                <p className="text-sm text-gray-300 whitespace-pre-line line-clamp-4">
                  {item.poem_content}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Update modal */}
        {selectedMedia && (
          <MediaPreviewModal
            isOpen={!!selectedMedia}
            onClose={() => setSelectedMedia(null)}
            mediaUrl={selectedMedia.url}
            mediaType={selectedMedia.type}
            poemTitle={selectedMedia.title}
            poemContent={selectedMedia.content}
          />
        )}
        
        {visibleItems < items.length && (
          <div className="flex justify-center mt-16">
            <button 
              onClick={() => setVisibleItems(prev => prev + 6)}
              className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/10 
                       transition-colors duration-300 backdrop-blur-sm"
            >
              View More Creations
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default Gallery;

// Add custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Add style tag to head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = scrollbarStyles;
  document.head.appendChild(style);
} 